import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { combineLatest, map, of, Subscription, switchMap, take, tap } from "rxjs";
import { filterNullOrUndefined } from "src/app/@operators/filter-null-or-undefined";
import { VersionService } from "src/app/core/services/version.service";
import { setWebSiteStarted } from "src/app/kiosk/state/kiosk.actions";
import { AppState, getState } from "src/app/state/app.state";
import { environment } from "src/environments/environment";
import { setShowScreenSaver, setMobileApp, setSelectedInsurer, setSelectedLanguage, setStartupProcessDone, setCardIdInUrl, setStartMethod } from "../../transaction-info/state/transaction-info.actions";
import { getCardId, getInsurerParamsLoaded, getSelectedInsurer, getSelectedLanguage } from "../../transaction-info/state/transaction-info.selectors";
import { loginWithCertificate, loginLight, setKioskSite } from "../state/authentication.actions";
import { getKioskSiteInit, getToken } from "../state/authentication.selectors";
import { getSysParamsloaded } from "src/app/kiosk/state/kiosk.selectors";
import { checkKioskMaintenance } from "../../maintenance/state/maintenance.actions";
import { loadInsurerParams } from "../../insurer/state/insurer.actions";
import { submitCardId, submitCardId_S4 } from "../../contract/state/contract.actions";
import { DataStorageService } from "assurcard-angular-library";

@Component({
    selector: "app-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
	private languageCode: string;
	private insurerId: string;
	private setCardNbr: string;
	private MobileApp: string;
	private mode: string;
	private startMethod: string;
	private state:AppState;
	private subs: Subscription[] = [];
	private versionFileChecked = false;
	constructor(private store: Store<AppState>, private route: ActivatedRoute, private versionService: VersionService,
		private dataStorage: DataStorageService) {
		//debugger;
		this.store.dispatch(setWebSiteStarted({ data: true }));

		this.subs.push(getState(this.store).subscribe( state => {
			this.state = state
		}));

		this.subs.push(
			this.route.queryParams.subscribe((params) => {
				if (Object.keys(params).length == 0) return;
				
				this.languageCode = params["setLanguage"] ? params["setLanguage"] : params["lang"];// 'FR'
				if (this.languageCode) this.languageCode = this.languageCode.toUpperCase();
				this.insurerId = params["ic"] ? params["ic"] : params["setInsurerId"];//'888'
				this.setCardNbr = params["setCardNbr"];
				if (this.setCardNbr) this.setCardNbr = this.setCardNbr.toUpperCase();
				this.MobileApp = params["MobileApp"];
				this.startMethod = params["startMethod"];
				if (!this.startMethod)
					this.startMethod = 'MANUAL';
				this.store.dispatch(setStartMethod({ data: this.startMethod }));

				this.mode = params["mode"];
				if (this.mode == "screensaver") {
					this.store.dispatch(setShowScreenSaver({ data: true }));
					return;
				}		
			})
		);
	}

	ngOnInit(): void {
		this.subs.push(
			this.dataStorage.get('CURRENT_LANG').pipe(
				take(1),
				switchMap((lang) => {
					const shouldRemove = this.mode === 'screensaver' || (lang != null && this.setCardNbr == null);
					if (shouldRemove) {
						return this.dataStorage.remove('CURRENT_LANG').pipe(map(() => null));
					}
					return of(lang);
				}),
				switchMap(() => {
					if (this.languageCode == null) {
						return this.dataStorage.get<string>('CURRENT_LANG')
						.pipe(
							tap((lang) => this.languageCode = lang)
						);
					}
				return of(null);
				}),
				tap(() => {
					// 🔽 After CURRENT_LANG logic, execute all the rest
					this.initAppLogic();
				})
			).subscribe()
		);
	}

	initAppLogic() {
		this.subs.push(this.versionService.CheckVersion()
		.subscribe( _ => {
			this.versionFileChecked = true; //To be sure that kioskMode flag is up to date
				if (this.state.authentication.token == undefined) {
					if (environment.kioskMode) {
						this.store.dispatch(loginWithCertificate());
					}
					else {
						this.store.dispatch(loginLight());
					}
				}
		}));

		this.subs.push(
			combineLatest([this.store.pipe(select(getToken), filterNullOrUndefined()), this.store.pipe(select(getSysParamsloaded))])
			.subscribe(([token, sysParamLoaded]) => {
					if (!this.versionFileChecked || !token || sysParamLoaded !== true)
						return;
					if (this.MobileApp) {
						this.store.dispatch(setMobileApp({ data: true }));
					}
					if (environment.kioskMode)
						this.store.dispatch(setKioskSite({ token: token }));
					//1) Check maintenance
					//2) Set language
					//3) Set insurer
					//4) Set card id
					this.store.dispatch(checkKioskMaintenance({ kioskId: !environment.kioskMode ? -1 : this.state.transactionInfo.kioskId }));
					if (this.languageCode)
						this.store.dispatch(setSelectedLanguage({ data: null, languageCode: this.languageCode }));

					if (this.insurerId)
						this.store.dispatch(setSelectedInsurer({ data: null, insurerId: this.insurerId }));
					if (this.setCardNbr != null && this.setCardNbr !== "") {
						this.store.dispatch(setCardIdInUrl({ data: this.setCardNbr }));
					}
				})
		);

		this.subs.push(
			combineLatest([this.store.pipe(select(getCardId)), this.store.pipe(select(getInsurerParamsLoaded)), this.store.pipe(select(getSelectedLanguage))])
				.subscribe(([cardId, insurerParamsLoaded, language]) => {
					
					if (!this.versionFileChecked || this.insurerId == null)
						return;

					if (this.insurerId != null && (/*cardId == null ||*/ insurerParamsLoaded == null))
						return;

					if (this.languageCode != null && language == null)
						return;

					this.store.dispatch(setStartupProcessDone({ data: true }));
				})
		);

		this.subs.push(
			combineLatest([this.store.pipe(select(getSelectedInsurer)), 
				this.store.pipe(select(getToken)),
				this.store.pipe(select(getSelectedLanguage)), 
				this.store.pipe(select(getKioskSiteInit))])
				.subscribe(([insurer, token, language, kioskSiteInitDone]) => {
					//token is present only to enter in this function (when token is set, we will step in and versionFileChecked will be true)
					if (!this.versionFileChecked)
						return;

					var startupProcessDone = true;
					if (this.languageCode && !language)
						startupProcessDone = false;
					if (this.insurerId && !insurer)
						startupProcessDone = false;
					if (environment.kioskMode && kioskSiteInitDone !== true)
						startupProcessDone = false;
					if (insurer) {
						startupProcessDone = false;
						//When insurer Or language was set => load insurer params
						this.store.dispatch(loadInsurerParams({ insurerId: insurer.insurerId, langCode: language?.languageCode }));
			
						if (language != null && this.setCardNbr) //When we have language, insurer and have card id => do submit card id
						{
							const s4Url = environment.kioskMode ? this.state.transactionInfo.selectedInsurer.specificKioskURL : this.state.transactionInfo.selectedInsurer.specificHomeURL;
							if (s4Url != "") {
								console.log("Login submit s4Url != empty")
								this.store.dispatch(
									submitCardId_S4({
										cardNumber: this.setCardNbr,
										insurer: this.state.transactionInfo.selectedInsurer,
										siteId: this.state.transactionInfo.defaultSiteId,
										hospitalNo: this.state.transactionInfo.defaultHospitalNo?.toString() ?? "",
										language: language,
										urls4: s4Url,
									})
								);
							}
							else {
								console.log("Login submit s4Url == empty")
								this.store.dispatch(
									submitCardId({
										cardNumber: this.setCardNbr,
										insurer: this.state.transactionInfo.selectedInsurer,
										siteId: this.state.transactionInfo.defaultSiteId,
										hospitalNo: this.state.transactionInfo.defaultHospitalNo?.toString() ?? "",
										language: language,
										insurerQuestionnaireId: this.state.transactionInfo.selectedInsurer.questionnaireId,
									})
								);
							}
						}
					}
					console.log("startupProcessDone",startupProcessDone);
					this.store.dispatch(setStartupProcessDone({ data: startupProcessDone }));
				})
		);
	}


	ngOnDestroy(): void {
		this.subs.forEach((_) => _.unsubscribe());
	}
}
